import { customFontUrl } from "@commerce/shopper/font-face";
import { shopperHead } from "@commerce/shopper/meta";
import { ShopperChat } from "@commerce/shopper/ShopperChat";
import { formatMoney } from "@commerce/shopper/types";
import { embedSearchSchema, pickAppearanceOverrides } from "@repo/embed";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { XIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { buildCsp } from "~/server/csp";
import { getShopperPageFn } from "~/server/shopper";

/**
 * The product a link opens on (`?product=<itemId>`, the Meta catalog item's `link`): its card
 * from the public API, when the store sells it and the retailer's restrictions let it
 * through. Null otherwise.
 */
const getLinkedProductFn = createServerFn({ method: "GET" })
	.validator(
		(data: { orgSlug: string; store?: string; itemId: string }) => data,
	)
	.handler(async ({ data }) => {
		const { app } = await import("@repo/api");
		const query = data.store
			? `?store=${encodeURIComponent(data.store)}`
			: "";
		const response = await app.request(
			`/api/public/${encodeURIComponent(data.orgSlug)}/products/${encodeURIComponent(data.itemId)}${query}`,
		);
		return response.ok
			? ((await response.json()) as {
					title: string;
					imageUrl: string | null;
					price: string;
					currency: string;
					inStock: boolean;
				})
			: null;
	});

export const Route = createFileRoute("/c/$orgSlug")({
	staticData: { retailerPage: true },
	validateSearch: embedSearchSchema,
	// The app's appearance overrides are part of the first paint, so the loader takes them.
	loaderDeps: ({ search }) => ({
		store: search.store,
		product: search.product,
		overrides: pickAppearanceOverrides(search),
	}),
	loader: async ({ params, deps }) => {
		const page = await getShopperPageFn({
			data: {
				orgSlug: params.orgSlug,
				store: deps.store,
				overrides: deps.overrides,
			},
		});
		if (!page) {
			throw notFound();
		}
		const linkedProduct = deps.product
			? await getLinkedProductFn({
					data: {
						orgSlug: params.orgSlug,
						itemId: deps.product,
						...(deps.store ? { store: deps.store } : {}),
					},
				})
			: null;
		return { ...page, linkedProduct };
	},
	head: ({ loaderData, params }) => {
		const head = shopperHead(loaderData, {
			title: loaderData
				? `${loaderData.assistantName} · ${loaderData.retailerName}`
				: "Shopping assistant",
			description: loaderData
				? `Tell it what you need and it fills your ${loaderData.retailerName} basket. By text or by voice.`
				: "",
			path: `/c/${params.orgSlug}`,
		});
		const font = loaderData?.appearance.customFont;
		// The retailer's typeface starts downloading with the page, not when CSS asks for it.
		return font
			? {
					...head,
					links: [
						...(head.links ?? []),
						{
							rel: "preload",
							href: customFontUrl(font),
							as: "font",
							type: "font/woff2",
							crossOrigin: "anonymous",
						},
					],
				}
			: head;
	},
	// One CSP for this page (app/start.ts adds none when a route set it): framable by the
	// retailer's allowed origins, no third-party scripts. Launch tokens ride in the URL
	// until the page swaps them, so no Referer ever carries them away.
	headers: ({ loaderData }) => ({
		"content-security-policy": buildCsp({
			frameAncestors: loaderData?.allowedOrigins ?? [],
			shopper: true,
		}),
		"referrer-policy": "no-referrer",
	}),
	component: ShopperChatPage,
});

function ShopperChatPage() {
	const page = Route.useLoaderData();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	// Stable: the session rebuilds its launch when this changes.
	const onLaunched = useCallback(
		(store: string | null, locale?: string) =>
			navigate({
				search: (current) => ({
					...current,
					lt: undefined,
					code: undefined,
					store: store ?? undefined,
					// The launch token's hint, so a reload keeps the language.
					locale: locale ?? current.locale,
				}),
				replace: true,
				// A preloaded Android web view is "visible" but draws no frames
				// until it is shown: a view transition there waits 4 s for one
				// and holds the first frames of an open in that time.
				viewTransition: false,
			}),
		[navigate],
	);
	return (
		<>
			{page.linkedProduct ? (
				<LinkedProduct product={page.linkedProduct} />
			) : null}
			<ShopperChat page={page} search={search} onLaunched={onLaunched} />
		</>
	);
}

/** The product the link opened on, over the chat until the shopper closes it. */
function LinkedProduct({
	product,
}: {
	product: {
		title: string;
		imageUrl: string | null;
		price: string;
		currency: string;
		inStock: boolean;
	};
}) {
	const [open, setOpen] = useState(true);
	if (!open) {
		return null;
	}
	return (
		<div className="fixed inset-x-4 top-[calc(env(safe-area-inset-top,0px)+4.5rem)] z-50 mx-auto flex max-w-sm items-center gap-3 rounded-2xl bg-(--shop-card) p-3 text-(--shop-ink) shadow-lg ring-1 ring-(--shop-brand-line)">
			{product.imageUrl ? (
				<img
					src={product.imageUrl}
					alt=""
					width={56}
					height={56}
					loading="lazy"
					className="size-14 shrink-0 rounded-xl object-cover"
				/>
			) : null}
			<div className="flex min-w-0 flex-1 flex-col">
				<p className="truncate font-medium text-sm">{product.title}</p>
				<p className="text-(--shop-muted) text-sm tabular-nums">
					{formatMoney(product.price, product.currency)}
				</p>
			</div>
			<button
				type="button"
				aria-label="Close"
				onClick={() => setOpen(false)}
				className="flex size-11 shrink-0 items-center justify-center rounded-(--shop-btn)"
			>
				<XIcon className="size-4" />
			</button>
		</div>
	);
}
